<template>
  <div>
    <v-row class="flex justify-start" no-gutters>
      <v-col class="col-lg-3 col-sm-6 col-xs-12">
        <m-select
          data-cy="loansStatusFilter"
          :value="selectedStatus"
          @input="value => (selectedStatus = value)"
          :placeholder="$t('dealer-bike.loans.table.lbl-filter-status')"
          :items="filterList"
          :showSelectedCounter="true"
          :multiple="true">
          <template v-slot:prepend-inner>
            <i class="icon-mapit icon-filter-status"></i>
          </template>
        </m-select>
      </v-col>
      <v-col class="col-lg-3 col-sm-6 col-xs-12 pl-2 pl-xs-0">
        <m-select
          data-cy="loansTypeFilter"
          :value="selectedType"
          @input="value => (selectedType = value)"
          :placeholder="$t('dealer-bike.loans.table.lbl-filter-type')"
          :items="typeList"
          :showSelectedCounter="true"
          :multiple="true">
          <template v-slot:prepend-inner>
            <i class="icon-mapit icon-filter-status"></i>
          </template>
        </m-select>
      </v-col>
      <v-spacer />
      <v-col class="col-lg-5 col-sm-12">
        <mapit-input
          uid="loansSearchFilter"
          :value="search"
          :placeholder="$t('dealer-bike.loans.table.lbl-search')"
          @changeValue="value => (search = value)"
          :clearable="true" />
      </v-col>
    </v-row>
    <v-data-table
      id="dealer-bike-loans-table-list"
      :loading="loading"
      :loading-text="$t('dealer-bike.loans.table.loading')"
      :headers="headers"
      :items="filteredLoans"
      sort-by="scheduledFor"
      :sort-desc="[true]"
      class="elevation-0 pt-4"
      :no-data-text="
        $t(
          filteredLoans && filteredLoans.length > 0
            ? 'dealer-bike.loans.table.no-data-search'
            : 'dealer-bike.loans.table.no-data',
          {search},
        )
      "
      :no-results-text="$t('dealer-bike.loans.table.no-data-search')"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'fas fa-angle-double-left',
        lastIcon: 'fas fa-angle-double-right',
        prevIcon: 'fas fa-angle-left',
        nextIcon: 'fas fa-angle-right',
        'items-per-page-text': $t('dealer-bike.loans.table.footer_num_x_page'),
        'items-per-page-options': [10, 25, 50, -1],
      }">
      <template #footer.page-text="props">
        {{ props.pageStart }}-{{ props.pageStop }} {{ $t('honda.registration.list.of') }} {{ props.itemsLength }}
      </template>

      <template v-slot:item.dealerBike.vehicle.model="{item}">
        <div class="vehicle py-4">
          <span class="vehicle-model" data-hover>{{ item.dealerBike.vehicle.model }}</span
          ><br />
          <span class="vehicle-color">{{ item.dealerBike.vehicle.registrationNumber }}</span>
        </div>
      </template>

      <template v-slot:item.usageType="{item}">
        <v-tooltip bottom>
          <template v-slot:activator="{on, attrs}">
            <v-chip class="ma-2" outlined label text-color="black" v-bind="attrs" v-on="on">
              {{
                item.usageType === UsageType.Demo
                  ? $t('dealer-bike.loans.type.demo')
                  : $t('dealer-bike.loans.type.courtesy')
              }}
            </v-chip>
          </template>
          <span>{{
            item.usageType === UsageType.Demo
              ? $t('dealer-bike.loans.table.tooltips.demo')
              : $t('dealer-bike.loans.table.tooltips.courtesy')
          }}</span>
        </v-tooltip>
      </template>

      <template v-slot:item.contact.phone="{item}">
        <v-tooltip bottom>
          <template v-slot:activator="{on, attrs}">
            <div class="vehicle text-overflow" v-bind="attrs" v-on="on">
              <span class="vehicle-model">{{ `${item.contact.firstName} ${item.contact.lastName}` }}</span>
              <br />
              <m-phone-label :value="item.contact.phone" class="vehicle-color" />
            </div>
          </template>
          <span class="vehicle-model">{{ `${item.contact.firstName} ${item.contact.lastName}` }}</span>
        </v-tooltip>
      </template>

      <template v-slot:item.scheduledFor="{item}">
        <span class="text-item">{{ toDateTimeFormat(item.scheduledFor) }}</span>
      </template>

      <template v-slot:item.returnedAt="{item}">
        <span class="text-item" v-if="item.returnedAt">{{ toDateTimeFormat(item.returnedAt) }}</span>
      </template>

      <template v-slot:item.createdBy="{item}">
        <div class="text-overflow">
          <v-tooltip bottom>
            <template v-slot:activator="{on, attrs}">
              <span
                v-bind="attrs"
                v-on="on"
                class="text-item"
                v-if="
                  item.return
                    ? item.return.attendedBy || item.createdBy
                    : item.handover
                      ? item.handover.attendedBy || item.createdBy
                      : item.createdBy
                "
                >{{
                  (item.return
                    ? item.return.attendedBy || item.createdBy
                    : item.handover
                      ? item.handover.attendedBy || item.createdBy
                      : item.createdBy
                  ).slice(
                    0,
                    (item.return
                      ? item.return.attendedBy || item.createdBy
                      : item.handover
                        ? item.handover.attendedBy || item.createdBy
                        : item.createdBy
                    ).indexOf('<'),
                  )
                }}</span
              >
            </template>
            <span
              class="text-item"
              v-if="
                item.return
                  ? item.return.attendedBy || item.createdBy
                  : item.handover
                    ? item.handover.attendedBy || item.createdBy
                    : item.createdBy
              "
              >{{
                (item.return
                  ? item.return.attendedBy || item.createdBy
                  : item.handover
                    ? item.handover.attendedBy || item.createdBy
                    : item.createdBy
                ).slice(
                  0,
                  (item.return
                    ? item.return.attendedBy || item.createdBy
                    : item.handover
                      ? item.handover.attendedBy || item.createdBy
                      : item.createdBy
                  ).indexOf('<'),
                )
              }}</span
            >
          </v-tooltip>
        </div>
      </template>

      <template v-slot:item.status="{item}">
        <div>
          <v-icon
            :class="`icon-status ${
              item.status === LoanStatus.Completed
                ? 'green-dot'
                : item.status === LoanStatus.HandedOver
                  ? 'yellow-dot'
                  : item.status === LoanStatus.Booked
                    ? 'blue-dot'
                    : 'red-dot'
            }`"
            >&#xe025;
          </v-icon>
          <span class="text-item">
            {{
              item.status === LoanStatus.Completed
                ? $t('dealer-bike.loans.status.completed')
                : item.status === LoanStatus.HandedOver
                  ? $t('dealer-bike.loans.status.handed-over')
                  : item.status === LoanStatus.Returned
                    ? $t('dealer-bike.loans.status.returned')
                    : item.status === LoanStatus.Started
                      ? $t('dealer-bike.loans.status.started')
                      : $t('dealer-bike.loans.status.booked')
            }}
          </span>
        </div>
      </template>

      <template v-slot:item.actions="{item}">
        <loans-table-actions
          :item="item"
          :tableList="dealerBikeLoansList"
          :loading="loading"
          @refreshList="$emit('refreshList')" />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'

import LoansTableActions from '@/components/dealer-bike/LoansTableActions'
import {LoanStatus, UsageType} from '@/types/dealer-bikes'
import {InputTypes} from '@/utils/constants'
import {toDateTimeFormat} from '@/utils/date-helper'

import MapitInput from '../general/MapitInput.vue'
export default {
  name: 'LoansTable',
  components: {
    LoansTableActions,
    MapitInput,
  },
  data() {
    return {
      InputTypes,
      toDateTimeFormat,
      UsageType,
      LoanStatus,
      headers: [
        {
          text: this.$t('dealer-bike.loans.table.model-plate'),
          align: 'left',
          value: 'dealerBike.vehicle.model',
          class: 'text-header',
          sortable: true,
        },
        {
          text: this.$t('dealer-bike.loans.table.created-by'),
          align: 'left',
          value: 'createdBy',
          class: 'text-header',
          sortable: true,
        },
        {
          text: this.$t('dealer-bike.loans.table.type'),
          align: 'left',
          value: 'usageType',
          class: 'text-header',
          sortable: true,
        },
        {
          text: this.$t('dealer-bike.loans.table.user-phone'),
          align: 'left',
          value: 'contact.phone',
          class: 'text-header',
          sortable: false,
        },
        {
          text: this.$t('dealer-bike.loans.table.delivery-date'),
          align: 'left',
          value: 'scheduledFor',
          class: 'text-header',
          sortable: true,
        },
        {
          text: this.$t('dealer-bike.loans.table.return-date'),
          align: 'left',
          value: 'returnedAt',
          class: 'text-header',
          sortable: true,
        },
        {
          text: this.$t('dealer-bike.loans.table.state'),
          align: 'left',
          class: 'text-header',
          value: 'status',
          sortable: true,
          sort: (a, b) => ((a, b) ? a.localeCompare(b) : b.localeCompare(a)),
        },
        {
          text: this.$t('dealer-bike.loans.table.actions'),
          align: 'center',
          class: 'text-header',
          sortable: false,
          value: 'actions',
        },
      ],
      search: '',
      selectedStatus: [],
      selectedType: [],
      filterList: Object.values(LoanStatus)
        .filter(ls => [LoanStatus.HandedOver, LoanStatus.Started, LoanStatus.Returned].includes(ls))
        .map(item => {
          return {text: this.$t(`dealer-bike.loans.status.${item.toLowerCase().replace('_', '-')}`), value: item}
        }),
      typeList: Object.values(UsageType).map(item => {
        return {text: this.$t(`dealer-bike.loans.table.tooltips.${item.toLowerCase()}`), value: item}
      }),
      dialogMarkHandOverLoanSigned: false,
      dialogMarkReturnLoanSigned: false,
      dialogCancelledLoan: false,
      isOpenDialogWaitingDocs: false,
      loanId: null,
      selectedLoan: null,
    }
  },
  computed: {
    ...mapGetters({
      loading: 'isLoadingDealerBikes',
      dealerBikeLoansList: 'getDealerBikeLoansList',
    }),
    filteredLoans() {
      return this.dealerBikeLoansList.filter(item => {
        const filterValidStatus = item.status !== LoanStatus.Completed && item.status !== LoanStatus.Cancelled
        const filterSelectedStatus = this.selectedStatus.length > 0 ? this.selectedStatus.includes(item.status) : true
        const filterSelectedType = this.selectedType.length > 0 ? this.selectedType.includes(item.usageType) : true
        const filterSearchString = this.search
          ? JSON.stringify([
              item.dealerBike.vehicle.registrationNumber,
              item.dealerBike.vehicle.model,
              item.dealerBike.vehicle.color,
              item.dealerBike.vehicle.vin,
              item.contact.phone,
              item.createdBy,
              `${item.contact.firstName} ${item.contact.lastName}`,
            ])
              .toLowerCase()
              .includes(this.search.toLowerCase())
          : true
        return filterValidStatus && filterSelectedStatus && filterSelectedType && filterSearchString
      })
    },
  },
}
</script>
<style>
.text-overflow {
  max-width: 150px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
